import { SimpleGrid, Text, Textarea, TextInput } from "@mantine/core";
import { SectionCard } from "../HomeBlockShared";
import { DESIGN_IMAGE, HomeBlock } from "../homeBlockData";
import { GalleryConfig, GalleryPhoto } from "../homeBlockConfig";
import classes from "../homeBlock.module.css";
import {
  CustomerImageOption,
  PickerRow,
  searchCustomerImages,
  SearchPicker,
} from "../RemotePickers";
import {
  Crop,
  EditableList,
  EditorLayout,
  fieldSetter,
  IconPicker,
  lucideIcon,
  newId,
  ThumbPicker,
  useBlockConfig,
} from "./EditorKit";

const IMG = DESIGN_IMAGE.gallery;

const SAMPLE_PHOTOS: [string, string, Crop][] = [
  ["Trải nghiệm mua hàng rất tuyệt vời!", "Hà Nội", { x: 15, y: 183, w: 243, h: 377 }],
  ["Sản phẩm chính hãng, dịch vụ rất tốt!", "Hải Phòng", { x: 265, y: 183, w: 251, h: 182 }],
  ["Mua sắm tại HACOM, luôn yên tâm!", "Bắc Ninh", { x: 524, y: 183, w: 242, h: 182 }],
  ["Giá tốt, nhiều ưu đãi!", "Bình Dương", { x: 774, y: 183, w: 244, h: 182 }],
  ["Trải nghiệm mua hàng rất chuyên nghiệp!", "Vinh", { x: 1028, y: 183, w: 238, h: 182 }],
  ["Nhân viên tư vấn rất nhiệt tình và chuyên nghiệp!", "TP. Hồ Chí Minh", { x: 265, y: 374, w: 205, h: 186 }],
  ["Build PC tại HACOM quá xịn!", "Đà Nẵng", { x: 478, y: 374, w: 194, h: 186 }],
  ["HACOM luôn là lựa chọn hàng đầu của mình!", "Cần Thơ", { x: 680, y: 374, w: 182, h: 186 }],
  ["Tư vấn rõ ràng, giao hàng nhanh!", "Thái Nguyên", { x: 869, y: 374, w: 193, h: 186 }],
  ["Dịch vụ hậu mãi rất chu đáo!", "Đồng Nai", { x: 1071, y: 374, w: 195, h: 186 }],
];

// Ảnh mẫu cắt từ thiết kế: chỉ để xem khi chưa có ảnh, không lưu
const SAMPLE_CROPS = new Map<string, Crop>(
  SAMPLE_PHOTOS.map(([, , crop], i): [string, Crop] => [`photo-${i + 1}`, crop])
);

const stat = (icon: string, value: string, description: string) => ({
  id: newId(),
  icon: lucideIcon(icon),
  value,
  description,
});

const initialConfig = (block: HomeBlock): GalleryConfig => {
  const sample = block.code === "home-customers";
  return {
    heading: {
      eyebrow: sample ? "KHÁCH HÀNG HACOM" : "",
      title: sample ? "CÙNG HÀNG TRIỆU KHÁCH HÀNG KIẾN TẠO TRẢI NGHIỆM" : block.name,
      highlight: sample ? "TỐT HƠN" : "",
      description: sample
        ? "Mỗi sản phẩm đến tay khách hàng là một câu chuyện. Cảm ơn bạn đã tin tưởng và lựa chọn HACOM."
        : "",
      thanks: sample ? "Cảm ơn bạn đã đồng hành!" : "",
      buttonLabel: sample ? "Xem thêm hình ảnh" : "",
      link: sample ? "/khach-hang" : "",
      footer: sample ? "CÙNG HACOM - NHIỀU HƠN CẢ MỘT SẢN PHẨM" : "",
    },
    stats: sample
      ? [
          stat("star", "99%", "Đánh giá tích cực"),
          stat("heart", "Hàng ngàn", "Câu chuyện hài lòng"),
          stat("camera", "10.000+", "Khoảnh khắc được chia sẻ"),
          stat("users", "Luôn đồng hành", "Trước - Trong - Sau mua hàng"),
        ]
      : [],
    photos: sample
      ? SAMPLE_PHOTOS.map(([quote, place], i) => ({
          id: `photo-${i + 1}`,
          customerImageId: null,
          image: null,
          quote,
          place,
        }))
      : [],
  };
};

const customerImageRow = (item: CustomerImageOption) => (
  <PickerRow
    image={item.urls[0] ?? null}
    title={item.branchName || "Chưa rõ chi nhánh"}
    sub={`${item.itemCode ? `${item.itemCode} · ` : ""}${item.urls.length} ảnh`}
  />
);

const EditGallery = ({ block }: { block: HomeBlock }) => {
  const [config, setConfig] = useBlockConfig(block, () => initialConfig(block));
  const { heading } = config;
  const patchHeading = (v: Partial<GalleryConfig["heading"]>) =>
    setConfig((c) => ({ ...c, heading: { ...c.heading, ...v } }));
  const setPhotos = fieldSetter(setConfig, "photos");

  const chosenRecords = new Set(
    config.photos.flatMap((p) => (p.customerImageId === null ? [] : [String(p.customerImageId)]))
  );

  return (
    <EditorLayout block={block}>
      <SectionCard title="Đầu block">
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={12}>
          <TextInput
            label="Nhãn nhỏ"
            value={heading.eyebrow}
            onChange={(e) => patchHeading({ eyebrow: e.currentTarget.value })}
          />
          <TextInput
            label="Tiêu đề"
            value={heading.title}
            onChange={(e) => patchHeading({ title: e.currentTarget.value })}
          />
          <TextInput
            label="Phần tô màu trong tiêu đề"
            value={heading.highlight}
            onChange={(e) => patchHeading({ highlight: e.currentTarget.value })}
          />
          <TextInput
            label="Lời cảm ơn viết tay"
            value={heading.thanks}
            onChange={(e) => patchHeading({ thanks: e.currentTarget.value })}
          />
        </SimpleGrid>
        <Textarea
          label="Mô tả"
          autosize
          minRows={2}
          value={heading.description}
          onChange={(e) => patchHeading({ description: e.currentTarget.value })}
        />
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing={12}>
          <TextInput
            label="Nhãn nút"
            value={heading.buttonLabel}
            onChange={(e) => patchHeading({ buttonLabel: e.currentTarget.value })}
          />
          <TextInput
            label="Link"
            value={heading.link}
            onChange={(e) => patchHeading({ link: e.currentTarget.value })}
          />
          <TextInput
            label="Dòng chữ cuối block"
            value={heading.footer}
            onChange={(e) => patchHeading({ footer: e.currentTarget.value })}
          />
        </SimpleGrid>
      </SectionCard>

      <SectionCard title="Chỉ số">
        <EditableList
          items={config.stats}
          onChange={fieldSetter(setConfig, "stats")}
          itemLabel={(s) => s.value}
          columns={[
            { label: "Icon", width: "170px" },
            { label: "Con số", width: "minmax(0, 0.8fr)" },
            { label: "Mô tả", width: "minmax(0, 1fr)" },
          ]}
          renderCells={(s, update) => (
            <>
              <IconPicker
                ariaLabel={`Icon ${s.value}`}
                value={s.icon}
                onChange={(value) => update({ icon: value })}
              />
              <TextInput
                aria-label="Con số"
                value={s.value}
                onChange={(e) => update({ value: e.currentTarget.value })}
              />
              <TextInput
                aria-label={`Mô tả ${s.value}`}
                value={s.description}
                onChange={(e) => update({ description: e.currentTarget.value })}
              />
            </>
          )}
          newItem={() => ({ id: newId(), icon: null, value: "", description: "" })}
          addLabel="Thêm chỉ số"
          max={4}
          minWidth={520}
        />
        <Text fz={12} className={classes.muted}>
          Chỉ nhập số liệu đã được xác nhận.
        </Text>
      </SectionCard>

      <SectionCard
        title="Ảnh khách hàng"
        right={
          <SearchPicker
            buttonLabel="Chọn từ Danh sách ảnh khách hàng"
            searchPlaceholder="Tìm theo chi nhánh, mã sản phẩm"
            search={searchCustomerImages}
            itemKey={(item) => String(item.id)}
            renderItem={customerImageRow}
            chosenKeys={chosenRecords}
            onPick={(item) =>
              setPhotos((list) => [
                ...list,
                {
                  id: newId(),
                  customerImageId: item.id,
                  image: item.urls[0] ?? null,
                  quote: "",
                  place: item.branchName,
                },
              ])
            }
          />
        }
      >
        <Text fz={13} className={classes.muted}>
          Ảnh đầu tiên hiện khổ lớn bên trái; các ảnh sau chia 2 hàng, trượt ngang khi nhiều hơn 9
          ảnh. Ảnh chọn từ Danh sách ảnh khách hàng lưu kèm id bản ghi.
        </Text>
        <EditableList
          items={config.photos}
          onChange={setPhotos}
          itemLabel={(p) => p.place}
          columns={[
            { label: "Ảnh", width: "72px" },
            { label: "Lời nhận xét", width: "minmax(0, 1.6fr)" },
            { label: "Nơi mua", width: "minmax(0, 0.8fr)" },
            { label: "Nguồn", width: "96px" },
          ]}
          renderCells={(p: GalleryPhoto, update) => (
            <>
              <ThumbPicker
                label={p.place}
                src={p.image}
                imageId={IMG}
                crop={SAMPLE_CROPS.get(p.id)}
                onPick={(url) => update({ image: url, customerImageId: null })}
                width={72}
              />
              <TextInput
                aria-label="Lời nhận xét"
                value={p.quote}
                onChange={(e) => update({ quote: e.currentTarget.value })}
              />
              <TextInput
                aria-label="Nơi mua"
                placeholder="Khách hàng tại..."
                value={p.place}
                onChange={(e) => update({ place: e.currentTarget.value })}
              />
              <Text fz={12} className={classes.muted}>
                {p.customerImageId !== null ? `Ảnh KH #${p.customerImageId}` : "Tải lên riêng"}
              </Text>
            </>
          )}
          newItem={() => ({ id: newId(), customerImageId: null, image: null, quote: "", place: "" })}
          addLabel="Thêm ảnh tải lên"
          max={20}
          minWidth={660}
        />
      </SectionCard>
    </EditorLayout>
  );
};

export default EditGallery;
